import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  IconButton,
  InputAdornment,
  createTheme,
  ThemeProvider,
} from '@mui/material';
import {
  Visibility, 
  VisibilityOff,
  ArrowBack,
} from '@mui/icons-material';
import axios from 'axios';
import SuccessDialog from '../components/SuccessDialog';

import { useAuth } from '../contexts/AuthContext';
import MetaTags from '../components/MetaTags';
import { useSharedTheme } from '../theme/useSharedTheme';
import { getApiUrl } from '../utils/apiUrl';

const validationSchema = yup.object({
  businessEmail: yup
    .string()
    .email('Invalid email format')
    .required('Business email is required'),
  adminFullName: yup
    .string()
    .required('Admin full name is required'),
  password: yup
    .string()
    .min(8, 'Password should be of minimum 8 characters length')
    .required('Password is required'),
  businessInfo: yup
    .string()
    .required('Business information is required'),
});

const AdminSignup: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const { isDarkMode, toggleTheme, theme } = useSharedTheme();
  
  // Success Dialog state
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successDialogConfig, setSuccessDialogConfig] = useState({
    title: '',
    message: '',
    autoCloseDelay: 3000,
  });

  const formik = useFormik({
    initialValues: {
      businessEmail: '',
      adminFullName: '',
      password: '',
      businessInfo: '',
    },
    validationSchema: validationSchema,
        onSubmit: async (values) => {
      console.log('Form submitted with values:', values);
      try {
        // Format the message for admin signup request
        const message = `ADMIN ACCOUNT REQUEST

Business Email: ${values.businessEmail}
Admin Full Name: ${values.adminFullName}
Password: [PROVIDED]

Business Information & Requirements:
${values.businessInfo}

This is an admin account request that requires approval.`;

        console.log('Sending request to:', `${getApiUrl()}/api/support/inquiry`);
        const response = await axios.post(`${getApiUrl()}/api/support/inquiry`, {
          email: values.businessEmail,
          message: message,
          type: 'signup'
        });
        
        console.log('Response received:', response.data);
        if (response.data.message) {
          setSuccessDialogConfig({
            title: 'Admin Account Request Submitted Successfully',
            message: `Your request for an Admin Account has been successfully submitted. Our team will review the details and get back to you soon at ${values.businessEmail}.`,
            autoCloseDelay: 5000,
          });
          setShowSuccessDialog(true);
          // Redirect to login page after successful signup with a delay to show the message
          setTimeout(() => {
            window.location.href = '/admin/login';
          }, 2000); // 2 second delay
        }
      } catch (error: any) {
        console.error('Signup error:', error);
        setSuccessDialogConfig({
          title: 'Signup Failed',
          message: error.response?.data?.message || 'Signup failed. Please try again.',
          autoCloseDelay: 3000,
        });
        setShowSuccessDialog(true);
      }
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <MetaTags 
        title="Admin Signup - LeadForge"
        description="Request administrative access to LeadForge. Submit your business information for admin account approval."
        keywords="admin signup, LeadForge, administrative access, business account, admin request"
      />
      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh',
          height: { xs: 'auto', md: '100vh' },
          overflow: { xs: 'auto', md: 'hidden' },
          background: isDarkMode 
            ? theme.palette.gradients.authDark
            : theme.palette.gradients.authLight,
          transition: 'background 0.5s ease-in-out',
          position: 'relative',
          fontFamily: '"Nunito Sans", sans-serif',
        }}
      >



        {/* Centered Form Container */}
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'center',
            width: '100%',
            minHeight: '100vh',
            height: { xs: 'auto', md: '100vh' },
            padding: { xs: 0, md: 4 },
            paddingTop: { xs: 0, md: 4 },
          }}
        >
          <Box 
            sx={{
              width: { xs: '100%', md: '80%' }, 
              maxWidth: { xs: 'none', md: 1000 }, 
              backgroundColor: isDarkMode ? '#2e2e30' : '#ffffff',
              border: { xs: 'none', md: '0.3px solid #B9B9B9' },
              borderRadius: { xs: 0, md: 4 },
              padding: { xs: '10px', md: 4 },
              marginBottom: { xs: 0, md: 0 },
              boxShadow: { xs: 'none', md: '0 20px 40px rgba(0, 0, 0, 0.1)' },
              position: 'relative',
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: { xs: '100vh', md: '670px' },
              transition: 'background-color 0.3s ease'
            }}
          >
            {/* Back Button - Top Left Corner of White Container */}
            <Box
              sx={{
                position: 'absolute',
                top: { xs: '2%', md: '1%' },
                left: { xs: '4%', md: '1%' },
                display: 'none',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'transparent',
                border: isDarkMode ? '1px solid #ffffff' : '1px solid #000000',
                borderRadius: 20,
                padding: 2,
                width: 40,
                height: 40,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                zIndex: 1000,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
              onClick={() => window.location.href = '/admin/login'}
            >
              <ArrowBack
                sx={{
                  color: isDarkMode ? '#ffffff' : '#1B4672',
                  fontSize: '20px',
                  transition: 'color 0.3s ease',
                }}
              />
            </Box>
            {/* Heading */}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: isDarkMode ? '#ffffff' : '#202224',
                mb: 2,
                textAlign: 'center',
                fontSize: { xs: '1.5rem', md: '2rem' },
                letterSpacing: '-0.11px',
                marginTop: '0',
                '@media (min-width: 0px)': {
                  marginTop: '0',
                },
                '&.MuiTypography-root': {
                  marginTop: '0',
                },
              }}
            >
              Request Admin Account
            </Typography>
                
            {/* Description */}
            <Typography
              variant="body1"
              sx={{
                color: isDarkMode ? '#ffffff' : '#666',
                mb: 4,
                textAlign: 'center',
                fontSize: { xs: '0.9rem', md: '16px' },
                letterSpacing: '-0.06px',
                opacity: 0.8,
                maxWidth: { xs: '90%', md: '500px' },
                lineHeight: 1.4,
              }}
            >
                  Request administrative access to LeadForge. Our team will review your request and set up your admin account with full system privileges.
                </Typography>

            {/* Form */}
            <Box sx={{ width: '100%', maxWidth: { xs: 'none', md: '800px' } }}>
              <form onSubmit={formik.handleSubmit}>
                {/* Two Column Layout */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 4 }, mb: 4 }}>
                  {/* Left Column */}
                  <Box sx={{ flex: 1 }}>
                    {/* Business Email */}
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          color: isDarkMode ? '#ffffff' : '#202224',
                          mb: 1,
                          fontSize: '0.95rem',
                        }}
                      >
                        Business Email Address:
                      </Typography>
                      <TextField
                        fullWidth
                        id="businessEmail"
                        name="businessEmail"
                        placeholder="admin@company.com"
                        value={formik.values.businessEmail}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.businessEmail && Boolean(formik.errors.businessEmail)}
                        helperText={formik.touched.businessEmail && formik.errors.businessEmail}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '30px',
                            backgroundColor: `${isDarkMode ? '#2e2e30' : '#f8fafc'}`,
                            border: `${isDarkMode ? 'none' : '1px solid #e2e8f0'}`,
                            '& fieldset': {
                              border: 'none',
                              display: 'none',
                            },
                            '& input': {
                              color: `${isDarkMode ? '#A6A6A6' : '#202224'}`,
                              fontSize: '1rem',
                              padding: '12px 16px',
                              backgroundColor: 'transparent',
                            },
                            '& input:-webkit-autofill': {
                              backgroundColor: `${isDarkMode ? '#2e2e30' : '#f8fafc'}`,
                              WebkitBoxShadow: `0 0 0 1000px ${isDarkMode ? '#2e2e30' : '#f8fafc'} inset`,
                              WebkitTextFillColor: `${isDarkMode ? '#A6A6A6' : '#202224'}`,
                            },
                            '& input:-webkit-autofill:hover': {
                              backgroundColor: `${isDarkMode ? '#2e2e30' : '#f1f5f9'}`,
                              WebkitBoxShadow: `0 0 0 1000px ${isDarkMode ? '#2e2e30' : '#f1f5f9'} inset`,
                            },
                            '& input:-webkit-autofill:focus': {
                              backgroundColor: `${isDarkMode ? '#2e2e30' : '#ffffff'}`,
                              WebkitBoxShadow: `0 0 0 1000px ${isDarkMode ? '#2e2e30' : '#ffffff'} inset`,
                            },
                            '& input::placeholder': {
                              color: '#A6A6A6',
                              opacity: '1',
                            },
                            '&:hover': {
                              backgroundColor: `${isDarkMode ? '#2e2e30' : '#f1f5f9'}`,
                              border: `${isDarkMode ? 'none' : '1px solid #cbd5e1'}`,
                              '& fieldset': {
                                border: 'none',
                                display: 'none',
                              },
                            },
                            '&.Mui-focused': {
                              backgroundColor: `${isDarkMode ? '#2e2e30' : '#ffffff'}`,
                              border: '1px solid #6052A2',
                              boxShadow: '0 0 0 3px rgba(96, 82, 162, 0.1)',
                              '& fieldset': {
                                border: 'none',
                                display: 'none',
                              },
                            },
                          },
                        }}
                      />
              </Box>

                    {/* Admin Full Name */}
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          color: isDarkMode ? '#ffffff' : '#202224',
                          mb: 1,
                          fontSize: '0.95rem',
                        }}
                      >
                        Admin Full Name:
                      </Typography>
                    <TextField
                      fullWidth
                        id="adminFullName"
                        name="adminFullName"
                        placeholder="John Doe"
                        value={formik.values.adminFullName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.adminFullName && Boolean(formik.errors.adminFullName)}
                        helperText={formik.touched.adminFullName && formik.errors.adminFullName}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '30px',
                            backgroundColor: `${isDarkMode ? '#2e2e30' : '#f8fafc'}`,
                            border: `${isDarkMode ? 'none' : '1px solid #e2e8f0'}`,
                            '& fieldset': {
                              border: 'none',
                              display: 'none',
                            },
                            '& input': {
                              color: `${isDarkMode ? '#A6A6A6' : '#202224'}`,
                              fontSize: '1rem',
                              padding: '12px 16px',
                              backgroundColor: 'transparent',
                            },
                            '& input:-webkit-autofill': {
                              backgroundColor: `${isDarkMode ? '#2e2e30' : '#f8fafc'}`,
                              WebkitBoxShadow: `0 0 0 1000px ${isDarkMode ? '#2e2e30' : '#f8fafc'} inset`,
                              WebkitTextFillColor: `${isDarkMode ? '#A6A6A6' : '#202224'}`,
                            },
                            '& input:-webkit-autofill:hover': {
                              backgroundColor: `${isDarkMode ? '#2e2e30' : '#f1f5f9'}`,
                              WebkitBoxShadow: `0 0 0 1000px ${isDarkMode ? '#2e2e30' : '#f1f5f9'} inset`,
                            },
                            '& input:-webkit-autofill:focus': {
                              backgroundColor: `${isDarkMode ? '#2e2e30' : '#ffffff'}`,
                              WebkitBoxShadow: `0 0 0 1000px ${isDarkMode ? '#2e2e30' : '#ffffff'} inset`,
                            },
                            '& input::placeholder': {
                              color: '#A6A6A6',
                              opacity: '1',
                            },
                            '&:hover': {
                              backgroundColor: `${isDarkMode ? '#2e2e30' : '#f1f5f9'}`,
                              border: `${isDarkMode ? 'none' : '1px solid #cbd5e1'}`,
                              '& fieldset': {
                                border: 'none',
                                display: 'none',
                              },
                            },
                            '&.Mui-focused': {
                              backgroundColor: `${isDarkMode ? '#2e2e30' : '#ffffff'}`,
                              border: '1px solid #6052A2',
                              boxShadow: '0 0 0 3px rgba(96, 82, 162, 0.1)',
                              '& fieldset': {
                                border: 'none',
                                display: 'none',
                              },
                            },
                          },
                        }}
                      />
                    </Box>

                    {/* Password */}
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            color: isDarkMode ? '#ffffff' : '#202224',
                            fontSize: '0.95rem',
                          }}
                        >
                          Password
                        </Typography>
                        <Link
                          href="/admin/password-reset"
                          sx={{
                            color: isDarkMode ? '#ffffff' : '#666',
                            textDecoration: 'none',
                            fontSize: '0.85rem',
                            fontWeight: 400,
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          Forgot Password?
                        </Link>
                      </Box>
                    <TextField
                      fullWidth
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="????????"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.password && Boolean(formik.errors.password)}
                        helperText={formik.touched.password && formik.errors.password}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                onMouseDown={(e) => e.preventDefault()}
                                edge="end"
                                sx={{ 
                                  color: '#A6A6A6',
                                  backgroundColor: 'transparent',
                                  borderRadius: '0 30px 30px 0',
                                  marginRight: '-1px',
                                  marginTop: '-1px',
                                  marginBottom: '-1px',
                                  height: '48px',
                                  width: '48px',
                                  '&:hover': {
                                    backgroundColor: 'transparent',
                                  }
                                }}
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '30px',
                            backgroundColor: `${isDarkMode ? '#2e2e30' : '#f8fafc'}`,
                            border: `${isDarkMode ? 'none' : '1px solid #e2e8f0'}`,
                            '& fieldset': {
                              border: 'none',
                              display: 'none',
                            },
                            '& input': {
                              color: `${isDarkMode ? '#A6A6A6' : '#202224'}`,
                              fontSize: '1.2rem',
                              padding: '12px 16px',
                              backgroundColor: 'transparent',
                              fontFamily: 'monospace',
                              letterSpacing: '0.1em',
                            },
                            '& input:-webkit-autofill': {
                              backgroundColor: `${isDarkMode ? '#2e2e30' : '#f8fafc'}`,
                              WebkitBoxShadow: `0 0 0 1000px ${isDarkMode ? '#2e2e30' : '#f8fafc'} inset`,
                              WebkitTextFillColor: `${isDarkMode ? '#A6A6A6' : '#202224'}`,
                            },
                            '& input:-webkit-autofill:hover': {
                              backgroundColor: `${isDarkMode ? '#2e2e30' : '#f1f5f9'}`,
                              WebkitBoxShadow: `0 0 0 1000px ${isDarkMode ? '#2e2e30' : '#f1f5f9'} inset`,
                            },
                            '& input:-webkit-autofill:focus': {
                              backgroundColor: `${isDarkMode ? '#2e2e30' : '#ffffff'}`,
                              WebkitBoxShadow: `0 0 0 1000px ${isDarkMode ? '#2e2e30' : '#ffffff'} inset`,
                            },
                            '& input::placeholder': {
                              color: '#A6A6A6',
                              opacity: '1',
                            },
                            '&:hover': {
                              backgroundColor: `${isDarkMode ? '#2e2e30' : '#f1f5f9'}`,
                              border: `${isDarkMode ? 'none' : '1px solid #cbd5e1'}`,
                              '& fieldset': {
                                border: 'none',
                                display: 'none',
                              },
                            },
                            '&.Mui-focused': {
                              backgroundColor: `${isDarkMode ? '#2e2e30' : '#ffffff'}`,
                              border: '1px solid #6052A2',
                              boxShadow: '0 0 0 3px rgba(96, 82, 162, 0.1)',
                              '& fieldset': {
                                border: 'none',
                                display: 'none',
                              },
                            },
                          },
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Right Column */}
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: isDarkMode ? '#ffffff' : '#202224',
                        mb: 1,
                        fontSize: '0.95rem',
                      }}
                    >
                      Business Information & Requirements:
                    </Typography>
                    <TextField
                      fullWidth
                      id="businessInfo"
                      name="businessInfo"
                      multiline
                      rows={4}
                      placeholder="Please provide details about your business, why you need admin access, and any specific requirements..."
                      value={formik.values.businessInfo}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.businessInfo && Boolean(formik.errors.businessInfo)}
                      helperText={formik.touched.businessInfo && formik.errors.businessInfo}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                              backgroundColor: `${isDarkMode ? '#2e2e30' : '#f8fafc'}`,
                          border: `${isDarkMode ? 'none' : '1px solid #e2e8f0'}`,
                          '& fieldset': {
                            border: 'none',
                            display: 'none',
                          },
                          '& textarea': {
                            color: `${isDarkMode ? '#A6A6A6' : '#202224'}`,
                            fontSize: '1rem',
                            padding: '12px 16px',
                            backgroundColor: 'transparent',
                          },
                          '& textarea::placeholder': {
                            color: '#A6A6A6',
                            opacity: '1',
                          },
                          '&:hover': {
                            backgroundColor: `${isDarkMode ? '#2e2e30' : '#f1f5f9'}`,
                            border: `${isDarkMode ? 'none' : '1px solid #cbd5e1'}`,
                            '& fieldset': {
                              border: 'none',
                              display: 'none',
                            },
                          },
                          '&.Mui-focused': {
                            backgroundColor: `${isDarkMode ? '#2e2e30' : '#ffffff'}`,
                            border: '1px solid #6052A2',
                            boxShadow: '0 0 0 3px rgba(96, 82, 162, 0.1)',
                            '& fieldset': {
                              border: 'none',
                              display: 'none',
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                </Box>

                {/* Sign Up Button */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                                      <Button
                    type="submit"
                    variant="contained"
                    onClick={() => console.log('Sign Up button clicked')}
                    sx={{
                      py: 1.5,
                      px: 6,
                      borderRadius: 2,
                      background: isDarkMode 
                        ? '#ffffff' 
                        : '#1B4672',
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: isDarkMode ? '#1B4672' : 'white',
                      '&:hover': {
                        background: isDarkMode 
                          ? '#f0f0f0' 
                          : '#1B4672',
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.3s ease-in-out',
                    }}
                  >
                    Sign Up
                    </Button>
                </Box>
              </form>

              {/* Additional Info */}
              <Typography
                variant="body2"
                sx={{
                  textAlign: 'center',
                  mt: 2,
                  color: isDarkMode ? '#ffffff' : '#666',
                  fontSize: '12px',
                  lineHeight: 1.4,
                  maxWidth: '80%',
                  margin: '8px auto 0',
                }}
              >
                Admin accounts require approval and include full system access, client management, and administrative privileges.
                </Typography>
              </Box>

            {/* Bottom Container Row - Separate container like top containers */}
            <Box sx={{
              position: { xs: 'relative', md: 'absolute' },
              bottom: { xs: 'auto', md: 0 },
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: { xs: '10px 20px', md: '20px' },
              backgroundColor: 'transparent',
              mt: { xs: 2, md: 0 },
            }}>
              <Typography
                variant="body2"
                sx={{
                  textAlign: 'center',
                  color: isDarkMode ? '#ffffff' : '#666',
                  fontSize: '0.85rem',
                }}
              >
                Already have an account?{' '}
                <Link
                  href="/admin/login"
                  sx={{
                    color: isDarkMode ? '#ffffff' : '#1B4672',
                    textDecoration: 'underline',
                    fontWeight: 500,
                    fontSize: '16px',
                    '&:hover': {
                      color: isDarkMode ? '#ffffff' : '#1B4672',
                    },
                  }}
                >
                  Login
                </Link>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Success Dialog */}
      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title={successDialogConfig.title}
        message={successDialogConfig.message}
        buttonText="Got it"
        autoCloseDelay={2000}
      />
    </ThemeProvider>
  );
};

export default AdminSignup; 